import { motion, useScroll, useTransform } from "framer-motion";
import { ReactNode, useRef, useEffect, useState } from "react";
import { useSaveData } from "@/hooks/useSaveData";

/**
 * RevealImage – Pure Parallax
 *
 * Kein Fade, kein Clip-Path, kein Scale-In. Nur eine sanfte vertikale Verschiebung
 * basierend auf Scroll-Position. Funktioniert robust auf allen Geräten (auch Android,
 * Samsung Internet, In-App-Browser), weil ausschliesslich `transform: translateY()`
 * verwendet wird – die GPU-freundlichste und am breitesten unterstützte Eigenschaft.
 *
 * Auf Mobile/Save-Data/Reduced-Motion wird der Effekt deaktiviert (statisch),
 * um Performance und Lesbarkeit zu garantieren.
 */
const RevealImage = ({ children, className = "" }: { children: ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const saveData = useSaveData();
  const [enableParallax, setEnableParallax] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isDesktop = window.matchMedia("(min-width: 768px) and (hover: hover)").matches;
    setEnableParallax(isDesktop && !reducedMotion && !saveData);
  }, [saveData]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Sehr dezenter Parallax: ±24px über die gesamte Sichtbarkeit
  const y = useTransform(scrollYProgress, [0, 1], ["-24px", "24px"]);

  if (!enableParallax) {
    return (
      <div ref={ref} className={`overflow-hidden ${className}`}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        style={{
          y,
          willChange: "transform",
          // Bild leicht überdimensioniert, damit Parallax keine Lücken zeigt
          height: "calc(100% + 48px)",
          marginTop: "-24px",
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default RevealImage;
